// src/components/Admin/AdminGallery.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { galleryService } from '../../services/galleryService';
import MultiGalleryUpload from './MultiGalleryUpload';
import './AdminGallery.css';

const AdminGallery = ({ addNotification }) => {
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('images');
    const [categories, setCategories] = useState([]);
    const [galleryCategories, setGalleryCategories] = useState([]);
    const [formData, setFormData] = useState({
        media_type: 'image',
        title: '',
        description: '',
        file_url: '',
        thumbnail_url: '',
        category: '',
        featured: 0,
        order_position: 0
    });

    // ✅ Define loadAllMedia FIRST (before it's used in useEffect)
    const loadAllMedia = useCallback(async () => {
        setLoading(true);
        try {
            const imagesResponse = await galleryService.adminGetMediaByType('image');
            if (imagesResponse.success) {
                setImages(imagesResponse.media || []);
            } else {
                console.warn('Failed to load images:', imagesResponse.message);
                setImages([]);
            }
            
            const videosResponse = await galleryService.adminGetMediaByType('video');
            if (videosResponse.success) {
                setVideos(videosResponse.media || []);
            } else {
                console.warn('Failed to load videos:', videosResponse.message);
                setVideos([]);
            }
        } catch (error) {
            console.error('Error loading media:', error);
            if (addNotification) {
                addNotification('Failed to load media', 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    const loadCategories = useCallback(async () => {
        try {
            const response = await galleryService.getCategories();
            if (response.success) {
                setCategories(response.categories);
                setGalleryCategories(response.categories);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    }, []);

    // ✅ Now useEffect can safely use loadAllMedia and loadCategories
    useEffect(() => {
        loadCategories();
        loadAllMedia();
    }, [loadCategories, loadAllMedia]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title) {
            if (addNotification) addNotification('Title is required', 'error');
            return;
        }
        
        if (!formData.file_url) {
            if (addNotification) addNotification('Please upload a file first', 'error');
            return;
        }
        
        setUploading(true);
        let response;
        
        try {
            if (editingItem) {
                response = await galleryService.updateMedia(editingItem.id, formData);
            } else {
                response = await galleryService.createMedia(formData);
            }
            
            if (response.success) {
                if (addNotification) addNotification(`Media ${editingItem ? 'updated' : 'added'} successfully!`, 'success');
                setShowForm(false);
                setEditingItem(null);
                resetForm();
                await loadAllMedia();
            } else {
                if (addNotification) addNotification(response.message || 'Failed to save media', 'error');
            }
        } catch (error) {
            console.error('Submit error:', error);
            if (addNotification) addNotification('An error occurred', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this media?')) {
            try {
                const response = await galleryService.deleteMedia(id);
                if (response.success) {
                    if (addNotification) addNotification('Media deleted successfully!', 'success');
                    await loadAllMedia();
                } else {
                    if (addNotification) addNotification('Failed to delete media', 'error');
                }
            } catch (error) {
                console.error('Delete error:', error);
                if (addNotification) addNotification('An error occurred', 'error');
            }
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const fileType = file.type;
        const isImage = fileType.startsWith('image/');
        const isVideo = fileType.startsWith('video/');
        
        if (activeTab === 'images' && !isImage) {
            if (addNotification) addNotification('Please select an image file (jpg, png, gif, webp)', 'error');
            return;
        }
        
        if (activeTab === 'videos' && !isVideo) {
            if (addNotification) addNotification('Please select a video file (mp4, webm, mov)', 'error');
            return;
        }
        
        setUploading(true);
        try {
            const response = await galleryService.uploadFile(file);
            
            if (response.success) {
                setFormData({
                    ...formData,
                    media_type: activeTab === 'images' ? 'image' : 'video',
                    file_url: response.file_url,
                    thumbnail_url: response.thumbnail_url || response.file_url
                });
                if (addNotification) addNotification('File uploaded successfully!', 'success');
            } else {
                if (addNotification) addNotification(response.message || 'Failed to upload file', 'error');
            }
        } catch (error) {
            console.error('Upload error:', error);
            if (addNotification) addNotification('Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            media_type: activeTab === 'images' ? 'image' : 'video',
            title: '',
            description: '',
            file_url: '',
            thumbnail_url: '',
            category: '',
            featured: 0,
            order_position: 0
        });
    };

    const openAddModal = () => {
        resetForm();
        setEditingItem(null);
        setShowForm(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            media_type: item.media_type,
            title: item.title,
            description: item.description || '',
            file_url: item.file_url,
            thumbnail_url: item.thumbnail_url || '',
            category: item.category || '',
            featured: item.featured || 0,
            order_position: item.order_position || 0
        });
        setShowForm(true);
    };

    const getFullUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        if (url.startsWith('/')) return `https://nlngraclub.com${url}`;
        return `https://nlngraclub.com/${url}`;
    };

    const getCurrentMedia = () => {
        return activeTab === 'images' ? images : videos;
    };

    const getCurrentTotalViews = () => {
        const media = getCurrentMedia();
        return media.reduce((sum, item) => sum + (item.views || 0), 0);
    };

    // Refresh function for batch upload completion
    const refreshGalleryList = useCallback(() => {
        loadAllMedia();
    }, [loadAllMedia]);

    return (
        <div className="admin-gallery-modern">
            {/* Header */}
            <div className="gallery-header-modern">
                <div className="header-title-section">
                    <div className="title-icon">
                        <i className="fas fa-images"></i>
                    </div>
                    <div>
                        <h1>Gallery Management</h1>
                        <p>Manage your club's images and videos in one place</p>
                    </div>
                </div>
                <button className="btn-add-modern" onClick={openAddModal}>
                    <i className="fas fa-plus"></i>
                    <span>Add New</span>
                </button>
            </div>

            {/* Batch Upload Component */}
            <MultiGalleryUpload 
                categories={galleryCategories}
                onComplete={refreshGalleryList}
            />

            {/* Tab Navigation */}
            <div className="gallery-tabs-modern">
                <button 
                    className={`gallery-tab ${activeTab === 'images' ? 'active' : ''}`}
                    onClick={() => setActiveTab('images')}
                >
                    <i className="fas fa-image"></i>
                    <span>Images</span>
                    <span className="tab-count">{images.length}</span>
                </button>
                <button 
                    className={`gallery-tab ${activeTab === 'videos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('videos')}
                >
                    <i className="fas fa-video"></i>
                    <span>Videos</span>
                    <span className="tab-count">{videos.length}</span>
                </button>
            </div>

            {/* Stats Overview */}
            <div className="gallery-stats-modern">
                <div className="stat-card-modern">
                    <div className="stat-icon" style={{ background: '#e8f5e9' }}>
                        <i className="fas fa-image" style={{ color: '#2e7d32' }}></i>
                    </div>
                    <div className="stat-info">
                        <h3>{activeTab === 'images' ? images.length : videos.length}</h3>
                        <p>Total {activeTab === 'images' ? 'Images' : 'Videos'}</p>
                    </div>
                </div>
                <div className="stat-card-modern">
                    <div className="stat-icon" style={{ background: '#e3f2fd' }}>
                        <i className="fas fa-eye" style={{ color: '#1976d2' }}></i>
                    </div>
                    <div className="stat-info">
                        <h3>{getCurrentTotalViews().toLocaleString()}</h3>
                        <p>Total Views</p>
                    </div>
                </div>
                <div className="stat-card-modern">
                    <div className="stat-icon" style={{ background: '#fff3e0' }}>
                        <i className="fas fa-tag" style={{ color: '#f57c00' }}></i>
                    </div>
                    <div className="stat-info">
                        <h3>{categories.length}</h3>
                        <p>Categories</p>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading {activeTab}...</p>
                </div>
            ) : getCurrentMedia().length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className={activeTab === 'images' ? 'fas fa-image' : 'fas fa-video'}></i>
                    </div>
                    <h3>No {activeTab} found</h3>
                    <p>Get started by uploading your first {activeTab === 'images' ? 'image' : 'video'}</p>
                    <button className="btn-primary-modern" onClick={openAddModal}>
                        <i className="fas fa-upload"></i>
                        Upload {activeTab === 'images' ? 'Image' : 'Video'}
                    </button>
                </div>
            ) : (
                <div className="media-grid-modern">
                    {getCurrentMedia().map(item => (
                        <div key={item.id} className="media-card-modern">
                            {/* Media Preview */}
                            <div className="media-preview-modern">
                                {item.media_type === 'image' ? (
                                    <img 
                                        src={getFullUrl(item.thumbnail_url || item.file_url)} 
                                        alt={item.title}
                                        loading="lazy"
                                        onError={(e) => {
                                            e.target.src = '/images/placeholder-image.jpg';
                                            e.target.onerror = null;
                                        }}
                                    />
                                ) : (
                                    <div className="video-preview-modern">
                                        <video src={getFullUrl(item.thumbnail_url || item.file_url)} />
                                        <div className="play-icon-overlay">
                                            <i className="fas fa-play"></i>
                                        </div>
                                    </div>
                                )}
                                {item.featured === 1 && (
                                    <div className="featured-badge-modern">
                                        <i className="fas fa-star"></i>
                                        <span>Featured</span>
                                    </div>
                                )}
                            </div>

                            {/* Media Info */}
                            <div className="media-info-modern">
                                <h4 className="media-title">{item.title}</h4>
                                {item.description && (
                                    <p className="media-description">{item.description.substring(0, 80)}...</p>
                                )}
                                <div className="media-meta">
                                    {item.category && (
                                        <span className="meta-tag">
                                            <i className="fas fa-tag"></i> {item.category}
                                        </span>
                                    )}
                                    <span className="meta-tag">
                                        <i className="fas fa-eye"></i> {item.views || 0} views
                                    </span>
                                    <span className="meta-tag">
                                        <i className="fas fa-calendar"></i> {new Date(item.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="media-actions-modern">
                                <button 
                                    className="action-btn edit" 
                                    onClick={() => openEditModal(item)}
                                    title="Edit"
                                >
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button 
                                    className="action-btn delete" 
                                    onClick={() => handleDelete(item.id)}
                                    title="Delete"
                                >
                                    <i className="fas fa-trash-alt"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Modal */}
            {showForm && (
                <div className="modal-overlay-modern" onClick={() => setShowForm(false)}>
                    <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h3>
                                <i className={activeTab === 'images' ? 'fas fa-image' : 'fas fa-video'}></i>
                                {editingItem ? 'Edit Media' : `Add New ${activeTab === 'images' ? 'Image' : 'Video'}`}
                            </h3>
                            <button className="modal-close-modern" onClick={() => setShowForm(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form-modern">
                            <div className="form-row-modern">
                                <div className="form-group-modern full-width">
                                    <label>Title *</label>
                                    <input 
                                        type="text" 
                                        value={formData.title} 
                                        onChange={e => setFormData({...formData, title: e.target.value})} 
                                        required 
                                        placeholder="Enter a descriptive title"
                                    />
                                </div>
                            </div>

                            <div className="form-row-modern">
                                <div className="form-group-modern full-width">
                                    <label>Description</label>
                                    <textarea 
                                        value={formData.description} 
                                        onChange={e => setFormData({...formData, description: e.target.value})} 
                                        rows="3"
                                        placeholder="Enter a description for this media"
                                    />
                                </div>
                            </div>

                            <div className="form-row-modern">
                                <div className="form-group-modern">
                                    <label>Category</label>
                                    <select 
                                        value={formData.category} 
                                        onChange={e => setFormData({...formData, category: e.target.value})}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group-modern">
                                    <label>Featured Status</label>
                                    <select 
                                        value={formData.featured} 
                                        onChange={e => setFormData({...formData, featured: parseInt(e.target.value)})}
                                    >
                                        <option value="0">Not Featured</option>
                                        <option value="1">Featured</option>
                                    </select>
                                </div>

                                <div className="form-group-modern">
                                    <label>Display Order</label>
                                    <input 
                                        type="number" 
                                        value={formData.order_position} 
                                        onChange={e => setFormData({...formData, order_position: parseInt(e.target.value)})} 
                                        placeholder="0"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="form-row-modern">
                                <div className="form-group-modern full-width">
                                    <label>Upload {activeTab === 'images' ? 'Image' : 'Video'} File</label>
                                    <div className="file-upload-area-modern">
                                        <input 
                                            type="file" 
                                            id="file-upload"
                                            accept={activeTab === 'images' ? 'image/*' : 'video/*'} 
                                            onChange={handleFileUpload} 
                                            disabled={uploading}
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="file-upload" className="file-upload-label-modern">
                                            <i className="fas fa-cloud-upload-alt"></i>
                                            <span>{uploading ? 'Uploading...' : 'Click to select file'}</span>
                                            <small>Supported: {activeTab === 'images' ? 'JPG, PNG, GIF, WebP' : 'MP4, WebM, MOV'}</small>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {formData.file_url && (
                                <div className="preview-section-modern">
                                    <label>Preview</label>
                                    <div className="preview-container-modern">
                                        {formData.media_type === 'image' ? (
                                            <img src={getFullUrl(formData.file_url)} alt="Preview" />
                                        ) : (
                                            <video src={getFullUrl(formData.file_url)} controls />
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-cancel-modern" onClick={() => setShowForm(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-save-modern" disabled={uploading || !formData.file_url}>
                                    {uploading ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : (editingItem ? 'Update' : 'Save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminGallery;